import { gql } from '@apollo/client';
import { FormControl, FormLabel, useToast } from '@chakra-ui/react';
import { cloneDeep, isNil, merge } from 'lodash';
import { toAvailableControlToggleOptions } from '~components/displays/utils';
import {
  FormLabelPendingIndicator,
  isPropertyPending,
} from '~components/ui/FormLabelPendingIndicator';
import { Orientation } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { DisplayControlsToggle, ToggleOptionsConfig } from '../DisplayControlsToggle';
import {
  DisplayOrientationToggle_DisplayFragment,
  useUpdateOrientationMutation,
} from './__generated__/DisplayOrientationToggle.graphql';

interface Props {
  display: DisplayOrientationToggle_DisplayFragment;
}

const orientationToggleOptions: Array<ToggleOptionsConfig<Orientation>> = [
  { value: Orientation.Landscape, label: 'Landscape' },
  { value: Orientation.Portrait, label: 'Portrait' },
];

export function DisplayOrientationToggle({ display }: Props) {
  const toast = useToast();
  const [updateOrientation, { loading }] = useUpdateOrientationMutation();

  const handleChange = async (orientation: Orientation) => {
    try {
      await updateOrientation({
        variables: {
          input: {
            id: display.id,
            orientation,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          displayUpdateOrientation: {
            __typename: 'DisplayUpdateOrientationPayload',
            display: {
              __typename: 'Display',
              id: display.id,
              orientation: merge(cloneDeep(display.orientation), { desired: orientation }),
            },
          },
        },
      });
    } catch (error) {
      toast({
        status: 'error',
        title: 'Cannot update orientation',
        description: fromError(error, 'UpdateOrientation'),
      });
    }
  };

  const currentControlState = display.orientation?.desired ?? display.orientation?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    orientationToggleOptions,
    display.orientation?.supportedValues,
  );

  const isPending = isPropertyPending(display.orientation);

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <FormControl>
      <FormLabel>
        Screen orientation
        <FormLabelPendingIndicator isPending={isPending} />
      </FormLabel>

      <DisplayControlsToggle
        name="orientationControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
      />
    </FormControl>
  );
}

DisplayOrientationToggle.graphql = {
  fragments: {
    DisplayOrientationToggle_display: gql`
      fragment DisplayOrientationToggle_display on Display {
        id
        orientation {
          reported
          desired
          supportedValues
        }
      }
    `,
  },
  mutations: {
    UpdateOrientation: gql`
      mutation UpdateOrientation($input: DisplayUpdateOrientationInput!) {
        displayUpdateOrientation(input: $input) {
          display {
            id
            orientation {
              reported
              desired
              supportedValues
            }
          }
        }
      }
    `,
  },
};
